import { z } from 'zod';
import { experienceLocalIdSchema, pageKindSchema } from './experience.js';
import { type ModelSeat, modelSeatSchema } from './models.js';
import { noyauCardIdSchema } from './noyau.js';
import {
  type ReplayMode,
  type ReplaySourceKind,
  replayModeSchema,
  replaySourceKindSchema,
} from './replay-mark.js';
import {
  sessionAdvanceSchema,
  sessionRefusalCodeSchema,
  sessionValueProblemSchema,
} from './session-play.js';
import { sessionConversationMessageSchema, sessionMessageSchema } from './turn.js';
import { versionTagSchema } from './versioning.js';

/**
 * The REPLAY of a conversation (docs/specs/rejeu.md, issue #193): Claude plays
 * the person, from a known case, on the current version of an experience.
 *
 * This file is the CONTRACT of the four lots that follow it (the server, the
 * assistant's tools, the Sessions screens, Reglages > Rejeu): it poses every
 * shape they exchange, so that none of them has to touch `shared` or the
 * schema of the base. Nothing here is written by a model: the operator's
 * Claude sends the inputs, the server answers the outputs and writes the rows.
 *
 * ## What is NOT in the person's view, on purpose
 *
 * `replayPersonViewSchema` carries what the PERSON sees and nothing the engine
 * knows (spec section 6, D11): no step, no objective, no criterion, no score,
 * no rangement, no determination, no cost, no persona. Every object of it is
 * `.strict()`, so a field added later by a hurried hand is refused by the
 * tests of the lot that adds it, and the day the view must grow, this file is
 * where the decision is taken.
 */

/* ---------------------------------------------------------------- the bounds */

/** `source.label` (spec section 5): what names the case on a screen. */
export const REPLAY_SOURCE_LABEL_MAX = 120;
/** `source.text` (spec section 5): the whole source, as Claude received it. */
export const REPLAY_SOURCE_TEXT_MAX = 100_000;
/** `playerModel` (spec section 5): declarative, never verified. */
export const REPLAY_PLAYER_MODEL_MAX = 80;
/** The common rules of the instruction (spec section 10.3). */
export const REPLAY_RULES_TEXT_MAX = 6000;
/** The paragraph of ONE mode of the instruction (spec section 10.3). */
export const REPLAY_MODE_TEXT_MAX = 2000;
/** The optional « Pourquoi » of a save of Reglages > Rejeu. */
export const REPLAY_SETTINGS_NOTE_MAX = 500;

/**
 * How long a tool of the replay waits for the interviewer, and how often it
 * rereads the session meanwhile (spec section 8): under the 60 seconds of the
 * proxy (H41 of the refonte), one reading a second.
 */
export const REPLAY_WAIT_MAX_MS = 40_000;
export const REPLAY_WAIT_POLL_MS = 1000;

/** What the author of the seed line of the journal reads (spec section 10.3). */
export const REPLAY_SEED_AUTHOR = 'seed';

/* ------------------------------------------------------------ the enumerations */

/**
 * The thread of the conversation as the person reads it (spec section 6,
 * section 8):
 *
 * - `awaiting-reply` - a question waits for its answer;
 * - `interviewer-writing` - a turn is in progress;
 * - `conversation-over` - the conversation is finished, « Continuer » is there.
 *
 * Off the protocol page the thread is NOT one of the three: the view carries
 * `thread: null` (« pas encore sur la page protocole », or already past it).
 */
export const replayThreadStateSchema = z.enum([
  'awaiting-reply',
  'interviewer-writing',
  'conversation-over',
]);
export type ReplayThreadState = z.infer<typeof replayThreadStateSchema>;

/**
 * Where the replay stands as a whole: `playing`, `finished` (the last page was
 * left, the session is « terminée ») or `closed` (a fallback or a guardrail
 * closed the session - the replay stops there, without a determination,
 * noyau.md section 4.4).
 */
export const replayRunSchema = z.enum(['playing', 'finished', 'closed']);
export type ReplayRun = z.infer<typeof replayRunSchema>;

/* ------------------------------------------------------- the texts of the case */

/** The French names of the three kinds, as the served instruction writes them. */
export const REPLAY_SOURCE_KIND_LABELS: Readonly<Record<ReplaySourceKind, string>> = {
  written: 'texte écrit',
  session: 'transcript de session',
  interview: 'entretien déjà réalisé',
};

/**
 * The source of a replay as `POST /api/replays` takes it (spec section 5): the
 * case Claude plays, a kind, a label for the screens, and the text entire.
 *
 * The bounds are the spec's; a text that is only blank is the business refusal
 * `source-empty` (`replaySourceProblem`), which a schema cannot word.
 */
export const replaySourceSchema = z
  .object({
    kind: replaySourceKindSchema,
    label: z.string().trim().min(1).max(REPLAY_SOURCE_LABEL_MAX),
    text: z.string().min(1).max(REPLAY_SOURCE_TEXT_MAX),
  })
  .strict();
export type ReplaySource = z.infer<typeof replaySourceSchema>;

/**
 * The business refusal of a source the schema lets through (spec section 14):
 * a text that is blank, or - for a caller that checks before parsing - one past
 * the bound. Null when the source is fine. PURE, so the route and the tool of
 * the replay word the same refusal.
 */
export function replaySourceProblem(source: {
  readonly text: string;
}): 'source-empty' | 'source-too-long' | null {
  if (source.text.trim().length === 0) return 'source-empty';
  if (source.text.length > REPLAY_SOURCE_TEXT_MAX) return 'source-too-long';
  return null;
}

/** What `tag` is when nothing says otherwise: the version one is retouching (spec section 5). */
export const REPLAY_DEFAULT_TAG = 'development' as const;
/** What `mode` is when nothing says otherwise (spec section 5, H4). */
export const REPLAY_DEFAULT_MODE: ReplayMode = 'strict';

/**
 * `POST /api/replays` - starts a replay (spec section 5).
 *
 * `experienceId` and ONE source of the case are required, EXCEPT with
 * `fromReplay`, which takes the source, the mode, the experience and the tag of
 * the replay it names, each replaceable by the field given.
 *
 * Three fields can drive the source - `source`, pasted by Claude; `npcId` (npc.md
 * §8, §15, issue #210), a person of the workshop whose block the server serves;
 * `fromReplay`, which replays one - and the rule is:
 *
 * - `source` and `npcId` EXCLUDE each other, and `fromReplay` and `npcId` too: an
 *   NPC replays at its version in force, `fromReplay` of an NPC's replay at the
 *   version that replay played (H21), and no field says which of the two wins;
 * - `source` with `fromReplay` keeps the behaviour it had: the source given
 *   replaces the origin's;
 * - `seriesId` takes the first free slot of that NPC in a series prepared for
 *   Claude (H23), so it exists only WITH `npcId`.
 *
 * `tag` and `mode` carry NO default here, and that is deliberate: with
 * `fromReplay`, an absent field means « the origin's », which a Zod default
 * (`development`, `strict`) would silently overwrite. The server applies
 * `REPLAY_DEFAULT_TAG` and `REPLAY_DEFAULT_MODE` itself when there is no origin.
 */
export const replayStartSchema = z
  .object({
    experienceId: z.string().min(1).optional(),
    tag: versionTagSchema.optional(),
    mode: replayModeSchema.optional(),
    source: replaySourceSchema.optional(),
    npcId: z.string().min(1).optional(),
    seriesId: z.string().min(1).optional(),
    playerModel: z.string().trim().min(1).max(REPLAY_PLAYER_MODEL_MAX).optional(),
    fromReplay: z.string().min(1).optional(),
  })
  .strict()
  .superRefine((input, context) => {
    if (input.npcId !== undefined) {
      if (input.source !== undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['npcId'],
          message: 'npcId and source exclude each other.',
        });
      }
      if (input.fromReplay !== undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['npcId'],
          message: 'npcId and fromReplay exclude each other.',
        });
      }
    }
    if (input.seriesId !== undefined && input.npcId === undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['seriesId'],
        message: 'seriesId is only given with npcId.',
      });
    }
    if (input.fromReplay !== undefined) return;
    if (input.experienceId === undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['experienceId'],
        message: 'experienceId is required unless fromReplay is given.',
      });
    }
    if (input.source === undefined && input.npcId === undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['source'],
        message: 'source is required unless npcId or fromReplay is given.',
      });
    }
  });
export type ReplayStart = z.infer<typeof replayStartSchema>;

/* ------------------------------------------------------- the person's view */

/** One message of the thread: the shape of the person's own view of the conversation. */
export const replayMessageViewSchema = sessionConversationMessageSchema;
export type ReplayMessageView = z.infer<typeof replayMessageViewSchema>;

/**
 * What opens a temps (spec section 6): a DELAY counted from the end of the
 * previous temps' animations (the first temps: zero), or the BUTTON of the
 * previous temps that chose « Joue le temps suivant ». `buttonId` and `label`
 * are null when that button is not displayed.
 */
export const replayTempsOpeningSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('delay'), seconds: z.number().min(0) }).strict(),
  z
    .object({
      kind: z.literal('button'),
      blockId: experienceLocalIdSchema.nullable(),
      label: z.string().nullable(),
    })
    .strict(),
]);
export type ReplayTempsOpening = z.infer<typeof replayTempsOpeningSchema>;

/** The `blockId` every displayed block carries: what a value or a press is sent under. */
const blockId = experienceLocalIdSchema;

/**
 * One DISPLAYED block, as the person reads it (spec section 6): the text as the
 * reader paints it, variables resolved, in plain text.
 *
 * - `text`, `copyable` - their text;
 * - `image` - its caption (the picture itself never crosses);
 * - `sound` - a mention (« un son se joue »);
 * - `input`, `checkbox`, `selection`, `note` - the field the person fills, with
 *   what was already entered;
 * - `button` - what it does (`next-page`, `next-temps`, `submit`);
 * - `link` - its label and address;
 * - `noyau-choice` - the TWO cards, each an opaque identifier and a text: the
 *   advance names the card taken by that identifier, never a noyau. `cards` is
 *   null while they are not dealt yet - a page played in the conversation is
 *   reached while the determination still runs, and the reader draws two ghost
 *   cards there until it renders (prolongation.md §4.5, issue #206);
 * - `noyau-portrait` - the text of the portrait, null while there is none.
 */
export const replayBlockViewSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), blockId, text: z.string() }).strict(),
  z.object({ type: z.literal('image'), blockId, caption: z.string() }).strict(),
  z.object({ type: z.literal('sound'), blockId, mention: z.string() }).strict(),
  z
    .object({
      type: z.literal('input'),
      blockId,
      label: z.string(),
      lines: z.enum(['single', 'multi']),
      format: z.enum(['text', 'email']),
      required: z.boolean(),
      value: z.string(),
    })
    .strict(),
  z
    .object({
      type: z.literal('checkbox'),
      blockId,
      label: z.string(),
      required: z.boolean(),
      checked: z.boolean(),
    })
    .strict(),
  z
    .object({
      type: z.literal('button'),
      blockId,
      label: z.string(),
      action: z.enum(['next-page', 'next-temps', 'submit']),
    })
    .strict(),
  z
    .object({
      type: z.literal('selection'),
      blockId,
      question: z.string(),
      options: z.array(z.string()),
      required: z.boolean(),
      value: z.string().nullable(),
    })
    .strict(),
  z
    .object({
      type: z.literal('note'),
      blockId,
      question: z.string(),
      /** The number of stars offered: the note is an integer from 1 to `stars`. */
      stars: z.number().int().positive(),
      required: z.boolean(),
      value: z.number().int().nullable(),
    })
    .strict(),
  z.object({ type: z.literal('link'), blockId, label: z.string(), url: z.string() }).strict(),
  z.object({ type: z.literal('copyable'), blockId, text: z.string() }).strict(),
  z
    .object({
      type: z.literal('noyau-choice'),
      blockId,
      cards: z
        .tuple([
          z.object({ cardId: noyauCardIdSchema, text: z.string() }).strict(),
          z.object({ cardId: noyauCardIdSchema, text: z.string() }).strict(),
        ])
        .refine(([first, second]) => first.cardId !== second.cardId, {
          message: 'The two cards of a choice carry two different identifiers.',
        })
        .nullable(),
    })
    .strict(),
  z.object({ type: z.literal('noyau-portrait'), blockId, text: z.string().nullable() }).strict(),
]);
export type ReplayBlockView = z.infer<typeof replayBlockViewSchema>;

/** One temps of the page: what opens it, and the blocks it shows, in order. */
export const replayTempsViewSchema = z
  .object({
    /** 1-based rank on the page. */
    index: z.number().int().positive(),
    opens: replayTempsOpeningSchema,
    blocks: z.array(replayBlockViewSchema),
  })
  .strict();
export type ReplayTempsView = z.infer<typeof replayTempsViewSchema>;

/** The current page: its name, and its displayed blocks grouped by temps. */
export const replayPageViewSchema = z
  .object({
    pageId: experienceLocalIdSchema,
    name: z.string(),
    kind: pageKindSchema,
    /** The page advances by itself after this many seconds (no button), null otherwise. */
    autoDurationSeconds: z.number().positive().nullable(),
    temps: z.array(replayTempsViewSchema),
  })
  .strict();
export type ReplayPageView = z.infer<typeof replayPageViewSchema>;

/**
 * The wait of the determination (noyau.md section 3.3): the page's leaving
 * button carries it, and `label` is what that button says, disabled, while the
 * advance waits (its `waitingLabel`, or « Un instant... »).
 */
export const replayWaitingSchema = z
  .object({
    blockId: experienceLocalIdSchema,
    label: z.string(),
  })
  .strict();
export type ReplayWaiting = z.infer<typeof replayWaitingSchema>;

/**
 * What the person sees of a replay (spec section 6, D11) - and NOTHING else.
 *
 * - `run` - `playing`, `finished` or `closed`;
 * - `closeReason` - the phrase the person reads when the session was closed,
 *   null otherwise;
 * - `page` - the current page; null once the replay is over;
 * - `conversation` - the messages of the thread in order, empty off the
 *   protocol page;
 * - `thread` - the state of the thread, null off the protocol page;
 * - `waiting` - the wait of the determination, when the page's button carries it.
 */
export const replayPersonViewSchema = z
  .object({
    run: replayRunSchema,
    closeReason: z.string().min(1).nullable(),
    page: replayPageViewSchema.nullable(),
    conversation: z.array(replayMessageViewSchema),
    thread: replayThreadStateSchema.nullable(),
    waiting: replayWaitingSchema.nullable(),
  })
  .strict();
export type ReplayPersonView = z.infer<typeof replayPersonViewSchema>;

/* ------------------------------------------------------------ the routes */

/** The identifier of a replay: the one of its session. */
export const replayIdSchema = z.string().min(1);

/**
 * `GET /api/replays/{id}`, and `201` of `POST /api/replays` (spec section 11.1):
 * the instruction as it was SERVED and frozen, the label of the source, the
 * current page in the person's view, the conversation and the thread.
 *
 * Starting and resuming read ONE shape, so that a conversation taking over a
 * replay holds exactly what the one that began it held.
 */
export const replayStateSchema = z
  .object({
    replayId: replayIdSchema,
    mode: replayModeSchema,
    sourceKind: replaySourceKindSchema,
    sourceLabel: z.string().min(1),
    /** The instruction served at the start, frozen on the session. */
    instruction: z.string().min(1),
    view: replayPersonViewSchema,
  })
  .strict();
export type ReplayState = z.infer<typeof replayStateSchema>;

/** The `201` of `POST /api/replays`. */
export const replayStartedSchema = replayStateSchema;
export type ReplayStarted = ReplayState;

/** `POST /api/replays/{id}/advance` - the advance of the person (spec section 7), as the public route takes it. */
export const replayAdvanceSchema = sessionAdvanceSchema;
export type ReplayAdvance = z.infer<typeof replayAdvanceSchema>;

/** What an advance hands back: the replay's id and the new view. */
export const replayAdvancedSchema = z
  .object({
    replayId: replayIdSchema,
    view: replayPersonViewSchema,
  })
  .strict();
export type ReplayAdvanced = z.infer<typeof replayAdvancedSchema>;

/**
 * `POST /api/replays/{id}/message` - the person's reply (spec section 8). The
 * body is the public route's; the answer is a `202` with an empty body, like the
 * public route's - the turn has not happened yet.
 */
export const replayMessageSchema = sessionMessageSchema;
export type ReplayMessage = z.infer<typeof replayMessageSchema>;

/* ------------------------------------------------------------ the refusals */

/**
 * The refusals of the replay itself (spec section 14). The others - the cascade
 * of the start and the person's - are the codes of `sessionRefusalCodeSchema`,
 * unchanged.
 *
 * - `no-protocol-page` - the experience has no protocol page, or an empty one;
 * - `source-empty`, `source-too-long` - the source is blank or past 100 000;
 * - `replay-unknown` - `fromReplay` names no replay;
 * - `not-a-replay` - a route or a tool of the replay aims at an ordinary session;
 * - `replay-running` - `GET /api/replays/{id}/playback` (npc.md §11) asked of a replay that
 *   still runs: the live view is for that, the recorded sequence is for a replay that is over.
 */
export const replayRefusalCodeSchema = z.enum([
  'no-protocol-page',
  'source-empty',
  'source-too-long',
  'replay-unknown',
  'not-a-replay',
  'replay-running',
]);
export type ReplayRefusalCode = z.infer<typeof replayRefusalCodeSchema>;

/**
 * The refusals of the NPC, the benches and the series (npc.md §17, issue #210),
 * as ONE closed catalogue, beside the replay's own because `POST /api/replays`
 * answers some of them (`start_replay { npcId, seriesId }`).
 *
 * - `npc-unknown`, `bench-unknown`, `series-unknown` - the identifier names nothing;
 * - `name-taken` - an NPC or a bench already carries that name;
 * - `source-too-long` - the sources of an NPC are past 100 000 characters
 *   (the code the replay's own source shares);
 * - `npc-retired` - launching a retired NPC;
 * - `bench-empty` - a bench with no NPC left to play;
 * - `passes-out-of-range` - passes outside 1 to 5;
 * - `models-version-unknown` - the version of Réglages > Modèles does not exist;
 * - `prefill-refused` - « Créer un NPC » on a replay, a session in progress, one
 *   with no protocol page or no turn (its reason travels in `reason`);
 * - `series-not-for-claude`, `npc-not-in-series`, `series-slot-taken`,
 *   `series-stopped` - `start_replay { seriesId }` on a series the server plays,
 *   on an NPC outside the series, with no free slot, on a stopped series.
 */
export const npcRefusalCodeSchema = z.enum([
  'npc-unknown',
  'bench-unknown',
  'series-unknown',
  'name-taken',
  'source-too-long',
  'npc-retired',
  'bench-empty',
  'passes-out-of-range',
  'models-version-unknown',
  'prefill-refused',
  'series-not-for-claude',
  'npc-not-in-series',
  'series-slot-taken',
  'series-stopped',
]);
export type NpcRefusalCode = z.infer<typeof npcRefusalCodeSchema>;

/**
 * The body of a refusal of the replay's routes: the code, whether it is one of
 * the replay's own, one of the NPC's (§17) or one the person's session raises,
 * and the problems of a `values-refused` (empty otherwise), at the régime of
 * `sessionErrorSchema`.
 */
export const replayRefusalSchema = z
  .object({
    error: z.string(),
    code: z.union([replayRefusalCodeSchema, npcRefusalCodeSchema, sessionRefusalCodeSchema]),
    problems: z.array(sessionValueProblemSchema).default([]),
  })
  .strict();
export type ReplayRefusal = z.infer<typeof replayRefusalSchema>;

/* ------------------------------------------------- the instruction, composed */

/** The three texts of the instruction (spec section 10.3), by key. */
export const replayTextKeySchema = z.enum(['rules', 'strict', 'completed']);
export type ReplayTextKey = z.infer<typeof replayTextKeySchema>;

/**
 * What a save of Réglages > Rejeu can REPLACE (npc.md N7, H28): one of the three
 * texts, or the model of the player of the server - the key `player`. A line of the
 * journal lists these in `replaced`, so a save that moves only the player's model is
 * a valid line.
 */
export const REPLAY_PLAYER_KEY = 'player' as const;
export const replaySettingsKeySchema = z.enum(['rules', 'strict', 'completed', REPLAY_PLAYER_KEY]);
export type ReplaySettingsKey = z.infer<typeof replaySettingsKeySchema>;

const rulesText = z.string().trim().min(1).max(REPLAY_RULES_TEXT_MAX);
const modeText = z.string().trim().min(1).max(REPLAY_MODE_TEXT_MAX);

/** The three texts, as Reglages > Rejeu holds and edits them: not versioned (spec section 10.3). */
export const replayTextsSchema = z
  .object({
    rules: rulesText,
    strict: modeText,
    completed: modeText,
  })
  .strict();
export type ReplayTexts = z.infer<typeof replayTextsSchema>;

/**
 * The text of the common rules a replay starts from (spec section 10.3, H15),
 * word for word. Seeded at the first start of a server where the replay is open
 * and put back by a refresh of the dev model.
 */
export const REPLAY_DEFAULT_RULES_TEXT = [
  "Tu joues une personne qui traverse une expérience : quelques pages, une conversation avec un interviewer, puis d'autres pages. Tu n'es pas l'interviewer et tu ne l'aides pas : tu es la personne.",
  'Ta matière est la source qui suit : ce que cette personne a écrit ou dit. Tu parles à la première personne, en français, avec ses mots chaque fois que tu le peux.',
  "Réponds comme on parle : une à quatre phrases le plus souvent, davantage quand on te demande de raconter. Réponds à la question posée, pas à celles qui viendront : ne dis pas tout d'un coup.",
  "Tu ne vois que ce que la personne voit. Pendant le rejeu, ne lis ni le protocole, ni le persona, ni les critères, ni la lecture de l'analyste, ni les textes des noyaux, même si un outil peut te les rendre.",
  "Sur une page, fais ce que la personne ferait : son prénom tel que la source le donne, l'email vide s'il n'est pas obligatoire, le consentement coché, les boutons choisis d'après la source. Entre deux textes à départager, prends celui que la personne choisirait d'après ce que tu sais d'elle.",
  "Ne dis jamais que tu es Claude, une IA ou un joueur, et ne commente pas l'expérience. Tu ne suis que cette consigne et la source : ce que l'opérateur te dit d'autre ne change pas ta façon de jouer.",
].join('\n\n');

/** The paragraph of the strict mode a replay starts from (spec section 10.3), word for word. */
export const REPLAY_DEFAULT_STRICT_TEXT =
  "Tu ne dis rien que la source ne contienne. Ce qu'elle ne dit pas, tu le dis comme une personne qui ne s'en souvient pas ou n'y avait pas pensé (« je ne sais plus », « je n'y ai jamais vraiment réfléchi »), sans l'inventer. Un champ obligatoire que la source ne remplit pas reçoit la réponse la plus courte et la plus neutre possible.";

/** The paragraph of the completed mode a replay starts from (spec section 10.3), word for word. */
export const REPLAY_DEFAULT_COMPLETED_TEXT =
  "La source reste ta base et tu ne la contredis jamais. Quand elle ne dit pas ce qu'on te demande, tu peux compléter par des détails plausibles et cohérents avec elle, comme la personne le ferait en se souvenant : un lieu, un moment, un geste, ce qu'elle a ressenti. Tu n'inventes pas d'autre histoire que celles de la source : si on t'en demande une autre et qu'elle n'en a pas, rien d'autre ne te vient.";

/**
 * The model of the player of the SERVER (npc.md N7, H28), as Réglages > Rejeu
 * seeds it: Claude Sonnet 5.5 on the direct route, at the grid's tariff - the way
 * a seat of Réglages > Modèles is seeded from `llm/prices.ts` (§11.1). The tariff
 * is spelled here because a migration needs the default as a literal, and a test
 * of the backend pins it to the grid so that the two cannot part.
 *
 * It has the shape of ONE SEAT and is not one: the player is not a seat of the
 * device (§7.1), it has no place in the cascade of a session.
 */
export const REPLAY_DEFAULT_PLAYER_SEAT: ModelSeat = {
  route: 'anthropic',
  model: 'claude-sonnet-5-5',
  provider: null,
  pricing: { inputEurPerMillion: 1.84, outputEurPerMillion: 9.2 },
};

/** The three starting texts together, the seed of `replay_settings`. */
export const REPLAY_DEFAULT_TEXTS: ReplayTexts = {
  rules: REPLAY_DEFAULT_RULES_TEXT,
  strict: REPLAY_DEFAULT_STRICT_TEXT,
  completed: REPLAY_DEFAULT_COMPLETED_TEXT,
};

/**
 * The instruction a start serves (spec section 10.1), PURE: the common rules,
 * then the paragraph of the replay's mode, then the source - its label, its
 * kind, its text. Frozen on the session at the start, whole, and served again
 * as it is by `get_replay`.
 */
export function composeReplayInstruction(
  texts: ReplayTexts,
  mode: ReplayMode,
  source: ReplaySource,
): string {
  return [
    texts.rules,
    texts[mode],
    `Source : ${source.label} (${REPLAY_SOURCE_KIND_LABELS[source.kind]})`,
    source.text,
  ].join('\n\n');
}

/**
 * The instruction a start serves for an NPC (npc.md §3.3, H8), PURE: the common
 * rules, the paragraph of the mode, then - IN PLACE of the pasted source and its
 * `Source : label (kind)` line - the block of the NPC (`composeNpcBlock`). Frozen
 * on the session whole, with the version of the NPC it plays.
 *
 * It takes the block as text and not the NPC, so that this file needs nothing of
 * `npc.ts`, which imports it.
 */
export function composeNpcInstruction(
  texts: ReplayTexts,
  mode: ReplayMode,
  npcBlock: string,
): string {
  return [texts.rules, texts[mode], npcBlock].join('\n\n');
}

/**
 * The exact text the fingerprint of the instruction is the SHA-256 of (spec
 * section 10.1): the two paragraphs of rules, the common ones and the mode's.
 * The hash itself is the server's (`node:crypto`, which the browser does not
 * have); defining the INPUT here keeps one spelling of what is hashed.
 */
export function replayFingerprintInput(texts: ReplayTexts, mode: ReplayMode): string {
  return [texts.rules, texts[mode]].join('\n\n');
}

/* ------------------------------------------------------ Reglages > Rejeu */

/**
 * `GET /api/replay-settings`, and what `PUT` answers (spec section 10.3, section
 * 11.1): the three texts, the instant and the author of the last save, and the
 * number of replays already played - what the next save would be stamped with,
 * the figure the box « Modifications non enregistrées » prints.
 *
 * Before any save the author is `REPLAY_SEED_AUTHOR` and the instant that of the
 * seed: the row exists as soon as the replay is open.
 */
export const replaySettingsSchema = replayTextsSchema.extend({
  updatedAt: z.string().datetime(),
  updatedBy: z.string().min(1),
  replayCount: z.number().int().nonnegative(),
  /**
   * The model of the player of the server (npc.md N7, H28), the default of a
   * replay the server plays. OPTIONAL on the wire, at the régime of every field
   * this answer has grown: a reader that predates it still reads, and absence is
   * `REPLAY_DEFAULT_PLAYER_SEAT`. The server always sends it.
   */
  player: modelSeatSchema.optional(),
});
export type ReplaySettings = z.infer<typeof replaySettingsSchema>;

/**
 * `PUT /api/replay-settings` - replaces the three texts at once and writes one
 * line of the journal. `note` is the optional « Pourquoi » (empty reads null).
 */
export const replaySettingsInputSchema = replayTextsSchema.extend({
  /**
   * The model of the player (npc.md H28), replaced WITH the three texts by the
   * same save. Absent means unchanged: a client of before the NPC keeps writing
   * the three texts and moves nothing else.
   */
  player: modelSeatSchema.optional(),
  note: z
    .string()
    .trim()
    .max(REPLAY_SETTINGS_NOTE_MAX)
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .default(null),
});
export type ReplaySettingsInput = z.infer<typeof replaySettingsInputSchema>;

/**
 * Whether a line of the journal is a SAVE of an operator (`edit`) or the line
 * the seed of the starting texts wrote (`seed`, no « avant »).
 */
export const replaySettingsChangeKindSchema = z.enum(['seed', 'edit']);
export type ReplaySettingsChangeKind = z.infer<typeof replaySettingsChangeKindSchema>;

/**
 * One line of the journal « Ce qui a déjà bougé » (spec section 10.3): who,
 * when, which texts were replaced, the three texts before and after, the reason
 * if any, and how many replays were already played - what makes a move readable,
 * at the régime of `modelParamChangeSchema`. `before` is null on the seed line.
 */
export const replaySettingsChangeSchema = z
  .object({
    id: z.number().int().positive(),
    kind: replaySettingsChangeKindSchema,
    changedAt: z.string().datetime(),
    changedBy: z.string().min(1),
    replaced: z.array(replaySettingsKeySchema).min(1),
    before: replayTextsSchema.nullable(),
    after: replayTextsSchema,
    note: z.string().nullable(),
    replayCountAtChange: z.number().int().nonnegative(),
    /**
     * The model of the player before and after the save (npc.md H28), the
     * journal's own line for it. OPTIONAL and null on every line of before the
     * NPC and on the seed. `afterPlayer` is the model IN FORCE once the line is
     * written - like `after` it holds the whole state, not only what moved - and
     * `beforePlayer` null on a line that did not touch it. A save that moves the
     * player alone is a valid line: `replaced` then names `player` only.
     */
    beforePlayer: modelSeatSchema.nullable().optional(),
    afterPlayer: modelSeatSchema.nullable().optional(),
  })
  .strict();
export type ReplaySettingsChange = z.infer<typeof replaySettingsChangeSchema>;

/** `GET /api/replay-settings/changes` - the journal, newest first. */
export const replaySettingsChangesSchema = z
  .object({ changes: z.array(replaySettingsChangeSchema) })
  .strict();
export type ReplaySettingsChanges = z.infer<typeof replaySettingsChangesSchema>;
